import { useCallback } from "react";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { isRecord } from "@coworker/apprestructured/src/shared/helpers/object.helper";

/**
 * Result of parsing initial values from query parameters.
 *
 * @property initialValues - Parsed initialValues object, or null if missing/invalid
 * @property isInvalid - True if parsing failed due to malformed data; false if no data or valid data
 */
interface ParsedInitialValuesResult {
  initialValues: Record<string, unknown> | null;
  isInvalid: boolean;
}

/**
 * Parses and validates initial task values from URL query parameters.
 *
 * Handles JSON parsing, URL decoding, and validation of the data structure.
 *
 * @returns A parser function that accepts an encoded JSON string and returns a result
 *          containing the parsed  object and validation status
 *
 * @example
 * const parseInitialValues = useParsedInitialValues();
 * const { initialValues, isInvalid } = parseInitialValues(searchParams.get('initialValues'));
 *
 * if (isInvalid) {
 *   // Show error message to user
 * } else if (initialValues) {
 *   // Use initialValues.details to populate form
 * }
 */
export const useParsedInitialValues = () => {
  return useCallback((initialValues: string): ParsedInitialValuesResult => {
    // No query param provided - this is valid (task opened without initial values)
    if (!initialValues) {
      return { initialValues: null, isInvalid: false };
    }
    try {
      const parsedInitialValues = JSON.parse(initialValues);

      // Top-level value must be an object
      if (!isRecord(parsedInitialValues)) {
        reportMessageToSentry(
          "Invalid initialValues format in query params",
          { initialValues, parsedInitialValues },
          "warning"
        );
        return { initialValues: null, isInvalid: true };
      }

      const details = parsedInitialValues["details"] ?? {};
      // Details property exists but is not an object (e.g., string, number, array)
      if (!isRecord(details)) {
        reportMessageToSentry(
          "Invalid details format in initialValues",
          { initialValues, details },
          "warning"
        );
        return { initialValues: null, isInvalid: true };
      }
      return {
        initialValues: { ...parsedInitialValues, details },
        isInvalid: false,
      };
    } catch (error) {
      // JSON parsing failed
      reportMessageToSentry(
        "Failed to parse initialValues from query params",
        { initialValues, error: String(error) },
        "warning"
      );
      return { initialValues: null, isInvalid: true };
    }
  }, []);
};
